import { FC, ReactElement, useContext } from "react";
import { Route, Routes } from "react-router-dom";
import { PageWrapper } from "./Components/Wrapper/PageWrapper";
import { DashboardPage } from "./Pages/DashboardPage";
import MobileDeviceUploadPage from "./Pages/MobileDeviceUploadPage";
import { RPGPage } from "./Pages/RPGPage";
import { ISidebarType } from "./types/IBase";
import { StatementSection } from "./Components/Sections/Statements/StatementSection";
import { StatusChangeSection } from "./Components/Sections/StatusChange/StatusChangeSection";
import { DeleteSection } from "./Components/Sections/Delete/DeleteSection";
import { CaseUpdateSection } from "./Components/Sections/CaseUpdate/CaseUpdateSection";
import { GeneralRequestSection } from "./Components/Sections/GeneralRequest/GeneralRequestSection";
import { ComplaintSection } from "./Components/Sections/Complaint/ComplaintSection";
import { SetTempHolderPage } from "./Pages/SetTempHolderPage";
import { ChangePPHolderPage } from "./Pages/ChangePPHolderPage";
import { ChangeLegalRepPage } from "./Pages/ChangeLegalRepPage";
import { FarmContext } from "./AppContent";
import { UserContext } from "./Context/UserContext";
import { pageNoAccess } from "./utils/PageAccess";
import { InvoicesPage } from "./Pages/InvoicesPage";
import { RefactionPage } from "./Pages/RefactionPage";
import { PesticideProcurementsPage } from "./Pages/PesticideProcurementsPage";
import { CancelRequestInfo } from "./Components/CancelRequestInfo";
import { RealLandSection } from "./Components/Sections/RealLands/RealLandSection";
import { FieldsBookPage } from "./Pages/FieldsBook/FieldsBookPage";
import { FieldBookSingleFieldPage } from "./Pages/FieldsBook/FieldBookSingleFieldPage";
import { ProfitLossReportPage } from "./Pages/FieldsBook/ProfitLossReportPage";
import { FieldEntryReportPage } from "./Pages/FieldsBook/FieldEntryReportPage";
import { NotificationsPage } from "./Pages/NotificationsPage";
import ConsentManagementPage from "./Pages/consent/ConsentManagementPage";
import IndividualConsentManagementPage from "./Pages/consent/IndividualConsentManagementPage";
import ConsentPage from "./Pages/consent/ConsentPage";
import { ConsentRequestsPage } from "./Pages/consent/ConsentRequestsPage";

interface IRoute {
  path?: string;
  element?: ReactElement;
  sidebar?: ISidebarType;
  title?: string;
  helpButton?: {
    isOpen?: boolean;
  };
  noAccess?: boolean;
  noAccessMessage?: string;
}

export const Router: FC = () => {
  const { userInfo } = useContext(UserContext);
  const { farm } = useContext(FarmContext);

  const noAccess = pageNoAccess(farm, userInfo);

  const routes: IRoute[] = [
    {
      path: "/consent",
      title: "PageTitleCONSENT",
      element: <ConsentPage />,
    },
    {
      path: "/consent-management",
      title: "PageTitleConsentManagementPage",
      element: <ConsentManagementPage />,
      sidebar: "other",
    },
    {
      path: "/consent-management/individual",
      title: "PageTitleIndividualConsentManagement",
      element: <IndividualConsentManagementPage />,
      sidebar: "other",
    },
    {
      path: "/consent-requests",
      title: "ConsentRequestList",
      helpButton: {},
      sidebar: "other",
      element: <ConsentRequestsPage />,
    },

    { path: "/", element: <DashboardPage /> },
    {
      path: "/notifications",
      title: "Notifications",
      helpButton: {},
      sidebar: "other",
      element: <NotificationsPage />,
    },
    {
      path: "/edit",
      title: "PageTitleEDIT",
      helpButton: { isOpen: false },
      sidebar: "data",
      element: <RPGPage mode="EDIT" />,
      noAccess: noAccess.edit,
      noAccessMessage: noAccess.editNoAccessMessage,
    },
    {
      path: "/renew",
      title: "PageTitleRENEW",
      helpButton: {},
      sidebar: "data",
      element: <RPGPage mode="RENEW" />,
      noAccess: noAccess.renew,
    },
    {
      path: "/new",
      title: "PageTitleNEW",
      helpButton: {},
      sidebar: "data",
      element: <RPGPage mode="NEW" />,
      noAccess: noAccess.new,
    },
    {
      path: "/statements",
      title: "PageTitleSTATEMENTS",
      helpButton: {},
      sidebar: "other",
      element: <StatementSection />,
      noAccess: noAccess.statements,
    },
    {
      path: "/status-change",
      title: "PageTitleSTATUS_CHANGE",
      helpButton: {},
      sidebar: "other",
      element: <StatusChangeSection />,
      noAccess: noAccess.statusChange,
    },
    {
      path: "/delete",
      title: "PageTitleDELETE",
      helpButton: {},
      sidebar: "other",
      element: <DeleteSection />,
      noAccess: noAccess.delete,
    },
    {
      path: "/change-holder-pp",
      title: "PageTitleCHANGE_HOLDER_PP",
      helpButton: {},
      sidebar: "other",
      element: <ChangePPHolderPage />,
      noAccess: noAccess.changeHolderPP,
    },
    {
      path: "/set-temp-holder",
      title: "PageTitleSET_TEMP_HOLDER",
      helpButton: {},
      sidebar: "other",
      element: <SetTempHolderPage />,
      noAccess: noAccess.setTempHolder,
    },
    {
      path: "/change-holder-le",
      title: "PageTitleCHANGE_HOLDER_LE",
      helpButton: {},
      sidebar: "other",
      element: <ChangeLegalRepPage />,
    },
    {
      path: "/case-update",
      title: "PageTitleCASE_UPDATE",
      helpButton: {},
      sidebar: "other",
      element: <CaseUpdateSection />,
    },
    {
      path: "/general-request",
      title: "PageTitleGENERAL_REQUEST",
      helpButton: {},
      sidebar: "other",
      element: <GeneralRequestSection />,
    },
    {
      path: "/complaint",
      title: "PageTitleCOMPLAINT",
      helpButton: {},
      sidebar: "other",
      element: <ComplaintSection requestType="COMPLAINT" />,
    },
    {
      path: "/complaint-waiver",
      title: "PageTitleCOMPLAINT_WAIVER",
      helpButton: {},
      sidebar: "other",
      element: <ComplaintSection requestType="COMPLAINT_WAIVER" />,
    },
    {
      path: "/receipts",
      title: "FuelInvoices",
      helpButton: {},
      sidebar: "other",
      element: <InvoicesPage />,
    },
    // {
    //   path: "/refaction-2025",
    //   title: "Refaction2025",
    //   helpButton: {},
    //   sidebar: "other",
    //   element: <RefactionPage year={2025} />,
    // },
    {
      path: "refaction-2026",
      title: "Refaction2026",
      helpButton: {},
      sidebar: "other",
      element: <RefactionPage year={2026} />,
    },
    {
      path: "/pesticide-procurements",
      title: "PesticideProcurements",
      helpButton: {},
      sidebar: "other",
      element: <PesticideProcurementsPage />,
    },
    {
      path: "/cancel-request",
      element: <CancelRequestInfo />,
    },
    {
      path: "/real-lands",
      title: "RealLandTitle",
      helpButton: {},
      sidebar: "other",
      element: <RealLandSection />,
    },
    {
      path: "/fields-book",
      title: "FieldsBook",
      helpButton: {},
      sidebar: "other",
      element: <FieldsBookPage />,
    },
    {
      path: "/fields-book/:uuid",
      title: "Field",
      helpButton: {},
      sidebar: "other",
      element: <FieldBookSingleFieldPage />,
    },
    {
      path: "/fields-book/:uuid/profit-loss-report",
      title: "ProfitLossReport",
      helpButton: {},
      sidebar: "other",
      element: <ProfitLossReportPage />,
    },
    {
      path: "/fields-book/:uuid/report",
      title: "FieldEntryReport",
      helpButton: {},
      sidebar: "other",
      element: <FieldEntryReportPage />,
    },
    {
      path: "/fields-book/report",
      title: "FieldEntryReport",
      helpButton: {},
      sidebar: "other",
      element: <FieldEntryReportPage />,
    },
  ];
  const pubRoutes: IRoute[] = [
    { path: "/mobile-upload", element: <MobileDeviceUploadPage /> },
  ];

  return (
    <Routes>
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          index={!route.path}
          element={
            <PageWrapper
              title={route.title}
              sidebar={route.sidebar}
              helpButton={route.helpButton}
              noAccess={route.noAccess}
              noAccessMessage={route.noAccessMessage}
            >
              {route.element}
            </PageWrapper>
          }
        />
      ))}
      {pubRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          index={!route.path}
          element={route.element}
        />
      ))}
    </Routes>
  );
};
